import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2,
  CheckCircle2,
  Clock3,
  CreditCard,
  Download,
  Euro,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  Receipt,
  Search,
  Settings2,
  ShieldCheck,
  XCircle,
  WalletCards,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { ErrorBox, SkeletonPage } from '../ui/kit';
import './Payouts.css';
import PageHeader from '../components/PageHeader';
import { datumLangUndZeit } from '../lib/zeit';
import PayoutReview from './PayoutReview';   // ADMIN-ABRECHNUNG-PRUEFANSICHT-001: lesende Prüfansicht je Periode

// ZAHLUNGSABSCHLUSS-OWNERABSCHLUSS-20261012: „Zahlungsabschluss offen" nennt die sperrende Zeile — der
// Server liefert sie bereits (pending_settlement_blocker); abgeschlossen wird sie unter Zahlungsabschlüsse.
const SETTLEMENT_EVENT_TEXT = { ride_completion: 'Fahrtabschluss', cancellation_fee: 'Stornogebühr', no_show_fee: 'No-Show-Gebühr' };
const settlementBlockerText = (blocker) => {
  if (!blocker || !blocker.ride_id) return null;
  const what = blocker.kind === 'tip' ? 'Trinkgeld'
    : blocker.kind === 'card_attempt_without_outbox' ? 'Kartenversuch ohne Abschlusszeile'
      : (SETTLEMENT_EVENT_TEXT[blocker.settlement_event] || 'Kartenzahlung');
  const amount = Number.isFinite(Number(blocker.amount_cents)) && blocker.amount_cents != null
    ? ` ${(Number(blocker.amount_cents) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '';
  return `${what}${amount} · Fahrt ${String(blocker.ride_id).slice(0, 8)}`;
};

const HISTORY_FILTERS = [
  { key: 'all', label: 'Alle Vorgänge' },
  { key: 'calculated', label: 'Berechnet' },
  { key: 'ready', label: 'Freigegeben' },
  { key: 'exported', label: 'Exportiert' },
  { key: 'paid', label: 'Ausgezahlt' },
];

const STATE_LABELS = {
  calculated: 'Berechnet',
  ready: 'Freigegeben',
  exported: 'Exportiert',
  paid: 'Ausgezahlt',
};

const normalized = (value) => String(value || '').trim().toLocaleLowerCase('de-DE');
const money = (value) => `${Number(value || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;
const cents = (value) => money(Number(value || 0) / 100);
const initials = (value) => String(value || 'P').split(/\s+/)
  .map((part) => part[0]).join('').slice(0, 2).toUpperCase();
const localDate = (iso) => datumLangUndZeit(iso, { leer: '–' });

function saveBlob(response, filename, type) {
  const url = window.URL.createObjectURL(new Blob([response.data], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

function WeeklyRunHistory({ initialRuns, initialNext }) {
  const [runs, setRuns] = useState(initialRuns);
  const [next, setNext] = useState(initialNext);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const generation = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => () => { generation.current += 1; }, []);
  const loadMore = async () => {
    if (inFlight.current || next == null) return;
    const current = generation.current;
    inFlight.current = true;
    setLoading(true);
    setFailed(false);
    try {
      const response = await api.get(`/admin/payouts/reviews/runs?before=${next}`);
      if (current !== generation.current) return;
      setRuns((previous) => [...previous, ...response.data.runs]);
      setNext(response.data.next);
    } catch (_) {
      if (current === generation.current) setFailed(true);
    } finally {
      if (current === generation.current) { setLoading(false); inFlight.current = false; }
    }
  };
  return <section className="payouts-history payouts-run-history" aria-label="Lauf- und Blockerhistorie">
    <h2>Lauf- und Blockerhistorie</h2>
    <p>Gespeicherte Ergebnisse je Partner und Woche. Ein früherer Sperrgrund beschreibt den damaligen Lauf.</p>
    {runs.length === 0 && <p>Noch kein Lauf protokolliert.</p>}
    {runs.map((run) => <details key={run.id}>
      <summary>{localDate(run.started_at)} · {run.trigger_kind === 'admin' ? 'Manuell' : 'Automatisch'} · {run.created_count} erstellt · {run.blocked_count} blockiert</summary>
      <div className="payouts-table-wrap"><table>
        <thead><tr><th>Partner</th><th>Woche</th><th>Ergebnis / Grund</th></tr></thead>
        <tbody>{['blocked', 'created', 'skipped'].flatMap((kind) => (run.result?.[kind] || []).map((item, index) =>
          <tr key={`${kind}:${index}`}>
            <td>{item.company_name || item.partner?.companyName || item.partner_id}</td>
            <td>{item.period_label || (item.period_start && item.period_end
              ? `${localDate(item.period_start)} – ${localDate(new Date(Date.parse(item.period_end) - 60000).toISOString())}`
              : 'Im damaligen Lauf nicht gespeichert')}</td>
            <td>{kind === 'created' ? 'Prüfstand erstellt' : item.reason || 'Kein Grund gespeichert'}
              {(item.blockers || []).filter((reason) => reason !== item.reason).map((reason) => <p key={reason}>{reason}</p>)}</td>
          </tr>))}</tbody>
      </table></div>
    </details>)}
    {failed && <p role="alert">Ältere Läufe konnten nicht geladen werden. Bitte erneut versuchen.</p>}
    {next != null && <button type="button" disabled={loading} onClick={loadMore}>{loading ? 'Lädt…' : 'Ältere Läufe anzeigen'}</button>}
  </section>;
}

export default function Payouts() {
  const [balances, setBalances] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [runs, setRuns] = useState([]);
  const [runsNext, setRunsNext] = useState(null);
  const [jobActive, setJobActive] = useState(false);
  const [sepaStatus, setSepaStatus] = useState({ ready: false, missing: [] });
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [creating, setCreating] = useState(false);
  const [rejectCandidate, setRejectCandidate] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [exporting, setExporting] = useState(false);
  const [paidCandidate, setPaidCandidate] = useState(null);
  const [paidReference, setPaidReference] = useState('');
  const [reviewPayout, setReviewPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [balanceSearch, setBalanceSearch] = useState('');
  const [balanceLimit, setBalanceLimit] = useState(25);
  const [historySearch, setHistorySearch] = useState('');
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyLimit, setHistoryLimit] = useState(25);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      api.get('/admin/balances'),
      api.get('/admin/payouts'),
      api.get('/admin/payouts/reviews'),
    ]).then(([balanceResponse, payoutResponse, reviewResponse]) => {
      setBalances(balanceResponse.data.partners || []);
      setPayouts(payoutResponse.data.payouts || []);
      setReviews(reviewResponse.data.reviews || []);
      setRuns(reviewResponse.data.runs || []);
      setRunsNext(reviewResponse.data.next ?? null);
      setJobActive(reviewResponse.data.job_active === true);
      setSepaStatus(reviewResponse.data.sepa || { ready: false, missing: [] });
      setLoadError(false);
      setBalanceLimit(25);
      setHistoryLimit(25);
    }).catch(() => setLoadError(true)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setBalanceLimit(25); }, [balanceSearch]);
  useEffect(() => { setHistoryLimit(25); }, [historySearch, historyFilter]);

  const matchingBalances = useMemo(() => {
    const term = normalized(balanceSearch);
    return balances.filter((partner) => !term || normalized([
      partner.company_name, partner.name, partner.email, partner.id,
    ].join(' ')).includes(term));
  }, [balances, balanceSearch]);
  const shownBalances = matchingBalances.slice(0, balanceLimit);

  const matchingPayouts = useMemo(() => {
    const term = normalized(historySearch);
    return payouts.filter((payout) => (
      (historyFilter === 'all' || payout.status === historyFilter)
      && (!term || normalized([
        payout.company_name, payout.notes, payout.period_start, payout.period_end,
        payout.id, payout.status, payout.batch_id,
      ].join(' ')).includes(term))
    ));
  }, [payouts, historyFilter, historySearch]);
  const shownPayouts = matchingPayouts.slice(0, historyLimit);
  const selectedPartner = balances.find((partner) => partner.id === selectedPartnerId);

  const createPayout = async (storedPartnerId) => {
    const partnerId = storedPartnerId || selectedPartnerId;
    if (!partnerId) return toast.error('Partner auswählen');
    if (!storedPartnerId && selectedPartner?.settlement_ready !== true) {
      return toast.error(selectedPartner?.pending_settlement_reason || 'Offener Zahlungsabschluss blockiert die Abrechnung');
    }
    setCreating(true);
    try {
      const response = await api.post('/admin/payouts', {
        partner_id: partnerId,
      });
      toast.success(`Prüfstand erstellt: ${response.data.review.period_label}`);
      setSelectedPartnerId(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Abrechnung fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const approveReview = async (review) => {
    try {
      await api.post(`/admin/payouts/reviews/${review.id}/approve`);
      toast.success('Prüfstand unverändert – Abrechnung freigegeben');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Freigabe fehlgeschlagen');
    }
  };

  const rejectReview = async () => {
    if (!rejectCandidate || !rejectReason.trim()) return toast.error('Ablehnungsgrund ist Pflicht');
    try {
      await api.post(`/admin/payouts/reviews/${rejectCandidate.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Prüfstand abgelehnt; Korrektur und Neuberechnung sind jetzt möglich');
      setRejectCandidate(null);
      setRejectReason('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ablehnung fehlgeschlagen');
    }
  };

  const recalculateReview = async (review) => {
    try {
      await api.post(`/admin/payouts/reviews/${review.id}/recalculate`);
      toast.success('Prüfstand neu berechnet');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Neuberechnung fehlgeschlagen');
    }
  };

  const runWeekly = async () => {
    setCreating(true);
    try {
      await api.post('/admin/payouts/reviews/run', { idempotency_key: `admin:${Date.now()}` });
      toast.success('Älteste fällige Wochen wurden geprüft');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Wochenlauf fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const setWeeklyJob = async (active) => {
    try {
      const response = await api.patch('/admin/payouts/job', { active });
      setJobActive(response.data.active === true);
      toast.success(response.data.active ? 'Automatischer Wochenlauf eingeschaltet' : 'Automatischer Wochenlauf ausgeschaltet');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Jobstatus konnte nicht geändert werden');
    }
  };

  const exportSepa = async () => {
    if (!selectedForExport.length) return toast.error('Mindestens eine freigegebene Periode auswählen');
    setExporting(true);
    const stableIds = [...selectedForExport].sort();
    try {
      const response = await api.post('/admin/payouts/sepa', {
        period_ids: stableIds,
        idempotency_key: `admin-periods:${stableIds.join(',')}`,
      });
      const file = await api.get(`/admin/payouts/sepa?batch_id=${encodeURIComponent(response.data.batch_id)}`, {
        responseType: 'blob',
      });
      saveBlob(file, `${response.data.batch_id}.xml`, 'application/xml;charset=utf-8');
      toast.success(`SEPA-Datei erzeugt · ${response.data.export_hash.slice(0, 12)}`);
      setSelectedForExport([]);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'SEPA-Dateiexport fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const confirmPaid = async () => {
    const reference = paidReference.trim();
    if (!paidCandidate || !reference) return toast.error('Bankreferenz ist Pflicht');
    try {
      await api.patch(`/admin/payouts/${paidCandidate.id}/paid`, { reference });
      toast.success('Auszahlung mit Bankreferenz bestätigt');
      setPaidCandidate(null);
      setPaidReference('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Paid-Bestätigung abgelehnt');
    }
  };

  const download = async (id, path, filename, type = 'application/pdf') => {
    try {
      const response = await api.get(`/admin/payouts/${id}/${path}`, { responseType: 'blob' });
      saveBlob(response, filename, type);
    } catch (error) {
      toast.error('Dokument konnte nicht geladen werden');
    }
  };

  if (loading && balances.length === 0 && payouts.length === 0 && reviews.length === 0) return <SkeletonPage />;

  return (
    <div className="payouts-page">
      <PageHeader
        title="Abrechnung"
        description="Unveränderliche Wochenstände, Dokumente, Export und explizite Zahlungsbestätigung."
        actions={
        <div className="payouts-heading-badges">
          <span><Building2 size={12} /> {balances.length} Partner</span>
          <span className="is-pending"><Clock3 size={12} /> {payouts.filter((p) => p.status !== 'paid').length} offen</span>
          <span className="is-paid"><CheckCircle2 size={12} /> {payouts.filter((p) => p.status === 'paid').length} bezahlt</span>
          <button type="button" onClick={load} disabled={loading}>
            <RefreshCw size={12} className={loading ? 'is-spinning' : ''} /> Aktualisieren
          </button>
        </div>
      }
      />

      <section className="payouts-principle">
        <span><ShieldCheck size={16} /></span>
        <div>
          <strong>Buchungszeilen und gespeicherter Abschluss sind die alleinige Geldquelle</strong>
          <p>Diese Oberfläche berechnet keine Fahrt neu. Vor „Freigegeben“ müssen Buchungszeilen, Dokumente und Auszahlungsbetrag centgenau übereinstimmen.</p>
        </div>
        <i>Fail-closed</i>
      </section>

      {loadError && <ErrorBox text="Abrechnungsdaten konnten nicht vollständig geladen werden." onRetry={load} />}

      <section className="payouts-settings payouts-weekly-control">
        <header className="payouts-section-heading">
          <div><span className="payouts-section-icon"><Clock3 size={15} /></span><div><span className="payouts-overline">Montag ab 04:00 · Europe/Berlin</span><h2>Automatischer Wochenlauf</h2><p>Erstellt ausschließlich Prüfstände. Freigabe und Zahlung bleiben Owner-Aktionen.</p></div></div>
          <span className={`payouts-config-status ${jobActive ? 'is-paid' : 'is-pending'}`}>{jobActive ? 'Eingeschaltet' : 'Ausgeschaltet'}</span>
        </header>
        <div className="payouts-settings-body">
          <button type="button" onClick={runWeekly} disabled={creating}><RefreshCw size={13} /> Jetzt älteste Wochen prüfen</button>
          <button type="button" onClick={() => setWeeklyJob(!jobActive)}>{jobActive ? 'Automatik ausschalten' : 'Automatik einschalten'}</button>
          <p>{runs[0] ? `Letzter Lauf: ${localDate(runs[0].finished_at)} · ${runs[0].created_count} erstellt · ${runs[0].blocked_count} blockiert` : 'Noch kein protokollierter Lauf.'}</p>
          {!sepaStatus.ready && <p className="payouts-blocker"><strong>Freigabe blockiert:</strong> VOOGO-SEPA-Stammdaten fehlen ({sepaStatus.missing.join(', ')}).</p>}
        </div>
      </section>

      <section className="payouts-settings">
        <header className="payouts-section-heading">
          <div>
            <span className="payouts-section-icon"><Settings2 size={15} /></span>
            <div><span className="payouts-overline">Zahlungsdienstleister</span><h2>Gebührennachweis</h2></div>
          </div>
          <span className="payouts-config-status"><CreditCard size={12} /> Signiert bestätigt</span>
        </header>
        <div className="payouts-settings-body">
          <div className="payouts-setting-explainer">
            <span><CreditCard size={14} /></span>
            <p><strong>Zahlungsdienstleisterkosten aus signiertem Zahlungsbeleg</strong>Was der Zahlungsdienstleister für den Einzug berechnet. Jede Kartentransaktion übernimmt ausschließlich die tatsächlich bestätigte Gebühr des Zahlungsdienstleisters. Prozent- oder Fixwerte in dieser Oberfläche verändern keine Abrechnung.</p>
          </div>
        </div>
      </section>

      <section className="payouts-balances">
        <header className="payouts-section-heading payouts-table-heading">
          <div>
            <span className="payouts-section-icon"><Euro size={15} /></span>
            <div><span className="payouts-overline">Noch nicht zugeordnete Buchungszeilen</span><h2>Partnerstände</h2><p>Alle Buchungszeilen, die noch keiner Abrechnungsperiode zugeordnet sind. Plus heißt: VOOGO schuldet dem Partner. Minus heißt: der Partner schuldet VOOGO.</p></div>
          </div>
          <div className="payouts-table-tools">
            <label className="payouts-search"><Search size={14} /><span className="sr-only">Partner durchsuchen</span><input type="search" value={balanceSearch} onChange={(e) => setBalanceSearch(e.target.value)} placeholder="Partner, Kontakt oder ID…" /></label>
            <span className="payouts-visible-count"><b>{shownBalances.length}</b> von {matchingBalances.length} sichtbar</span>
          </div>
        </header>
        <div className="payouts-table-wrap">
          <table>
            <thead><tr>{['Partner', 'Kontostand bei VOOGO', 'Fahrten', 'Sperrstatus', 'Aktion'].map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {shownBalances.length === 0 ? <tr className="payouts-empty-row"><td colSpan="5">Keine Partnerstände</td></tr> : shownBalances.map((partner) => {
                const blocked = Boolean(partner.migration_blocked || partner.period_conflict
                  || partner.settlement_ready !== true);
                return <tr className={selectedPartnerId === partner.id ? 'is-selected' : ''} key={partner.id}>
                  <td><span className="payouts-partner"><i>{initials(partner.company_name)}</i><span><strong>{partner.company_name}</strong><small>{partner.name} · {partner.email}</small></span></span></td>
                  <td><strong className={(partner.balance || 0) >= 0 ? 'is-positive' : 'is-negative'}>{(partner.balance || 0) >= 0 ? '+' : ''}{money(partner.balance)}</strong></td>
                  <td>{partner.unassigned_events || 0}</td>
                  <td>{partner.period_conflict ? <span className="payouts-status is-pending">Periodenkonflikt</span> : partner.migration_blocked ? <span className="payouts-status is-pending">Owner-Klärung offen</span> : partner.settlement_ready !== true ? <span className="payouts-blocker-cell"><span className="payouts-status is-pending">Zahlungsabschluss offen</span>{settlementBlockerText(partner.pending_settlement_blocker) && <small>{settlementBlockerText(partner.pending_settlement_blocker)} · <a href="/settlements">Zahlungsabschlüsse öffnen</a></small>}</span> : <span className="payouts-status is-paid">Bereit</span>}</td>
                  <td><button type="button" disabled={blocked} className={selectedPartnerId === partner.id ? 'is-selected' : ''} onClick={() => setSelectedPartnerId(selectedPartnerId === partner.id ? null : partner.id)}>{selectedPartnerId === partner.id ? 'Ausgewählt' : 'Abrechnen'}</button></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        {matchingBalances.length > balanceLimit && <div className="payouts-load-more"><span>Noch {matchingBalances.length - balanceLimit} Partner</span><button type="button" onClick={() => setBalanceLimit((n) => n + 25)}>Weitere 25 anzeigen</button></div>}
        {selectedPartner && (
          <div className="payouts-create">
            <div className="payouts-create-copy"><span className="payouts-section-icon"><WalletCards size={15} /></span><div><span className="payouts-overline">Ausgewählter Partner</span><h3>Prüfstand für {selectedPartner.company_name}</h3><p>Das System wählt chronologisch die älteste abgeschlossene offene Woche.</p></div></div>
            <div className="payouts-create-fields">
              <button type="button" onClick={() => createPayout()} disabled={creating || selectedPartner.settlement_ready !== true}>{creating ? 'Wird geprüft…' : 'Älteste Woche prüfen'}</button>
            </div>
          </div>
        )}
      </section>

      <section className="payouts-history payouts-reviews">
        <header className="payouts-section-heading"><div><span className="payouts-section-icon"><ShieldCheck size={15} /></span><div><span className="payouts-overline">Vor dem unveränderlichen Geldbuch</span><h2>Wochen-Prüfstände</h2><p>Fahrten, Zahlungsarten, Gebühren und Korrekturen sind vor der Freigabe vollständig sichtbar.</p></div></div></header>
        {reviews.length === 0 ? <p>Noch kein Prüfstand vorhanden.</p> : reviews.map((review) => {
          if (review.status === 'waiting') return <article className="payouts-review-card" key={review.id}>
            <header><div><strong>{review.company_name}</strong><small>{review.period_label}</small></div><span className="payouts-status is-pending">Wartende Woche</span></header>
            <p>{review.waiting_reason}</p><p>Beträge und Anfangssaldo werden erst nach Abschluss der Vorwoche festgestellt.</p>
          </article>;
          const b = review.breakdown || {};
          const providers = b.pspProviderFees || {};
          return <article className="payouts-review-card" key={review.id}>
            <header><div><strong>{review.company_name}</strong><small>{review.period_label}</small></div><span className={`payouts-status ${review.status === 'approved' ? 'is-paid' : 'is-pending'}`}>{review.status === 'pending' ? 'Zu prüfen' : review.status === 'rejected' ? 'Abgelehnt' : 'Freigegeben'}</span></header>
            <div className="payouts-review-grid">
              <span><small>Anfangssaldo</small><strong>{cents(review.carry_in_cents)}</strong></span>
              <span><small>Fahrten</small><strong>{b.rideCount || 0}</strong><small>{b.completedRideCount || 0} abgeschlossen · {b.cancellationRideCount || 0} Storno · {b.noShowRideCount || 0} Nichtantritt</small></span>
              <span><small>Bar</small><strong>{cents(b.cashCents)}</strong></span>
              <span><small>Online eingezogen</small><strong>{cents(b.collectedOnlineCents)}</strong></span>
              <span><small>Guthaben</small><strong>{cents(b.walletCents)}</strong></span>
              <span><small>VOOGO-Anteil</small><strong>{cents((b.takeGrossCents || 0) + (b.serviceGrossCents || 0))}</strong></span>
              <span><small>Zahlungsdienstleisterkosten</small><strong>{cents(b.pspCostCents)}</strong><small>Stripe {providers.stripe?.transactionCount || 0} · {cents(providers.stripe?.feeCents)} · PayPal {providers.paypal?.transactionCount || 0} · {cents(providers.paypal?.feeCents)}</small></span>
              <span><small>Korrekturen</small><strong>{cents(b.correctionCents)}</strong></span>
              <span><small>Auszahlungsbetrag</small><strong>{cents(review.payout_cents)}</strong><small>Schlusssaldo {cents(review.carry_out_cents ? -review.carry_out_cents : 0)}</small></span>
            </div>
            <details><summary>{review.lines.length} Buchungszeilen und Fahrten anzeigen</summary><div className="payouts-review-lines">{review.lines.map((line) => <div key={line.id}><span>{localDate(line.occurredAt)}</span><span>{line.eventType}</span><span>{line.rideId || 'Korrektur ohne Fahrt'}</span><span>{line.driverName || 'Partnerkonto'}</span><strong>{cents(line.amountCents)}</strong><small>{line.paymentProvider ? `${line.paymentProvider === 'stripe' ? 'Karte/Stripe' : 'PayPal'} · ${cents(line.paymentProviderFeeCents)}` : (line.correctionReason || line.components?.reason || '')}</small></div>)}</div></details>
            {review.rejection_reason && <p><strong>Ablehnungsgrund:</strong> {review.rejection_reason}</p>}
            <button type="button" className="payouts-review-open" aria-label={`Prüfstand prüfen: ${review.company_name}, ${review.period_label}`} onClick={() => setReviewPayout({ ...review, review_id: review.id })}><Search size={12} /> Prüfstand prüfen</button>
            {review.status !== 'approved' && <footer>
              <button type="button" onClick={() => recalculateReview(review)}><RefreshCw size={12} /> Neu berechnen</button>
              {review.status === 'pending' && <><button type="button" onClick={() => { setRejectCandidate(review); setRejectReason(''); }}><XCircle size={12} /> Ablehnen</button><button type="button" className="is-paid-action" disabled={!sepaStatus.ready} onClick={() => approveReview(review)}><ShieldCheck size={12} /> Freigeben</button></>}
            </footer>}
          </article>;
        })}
      </section>

      <WeeklyRunHistory key={runs[0]?.id || 'no-runs'} initialRuns={runs} initialNext={runsNext} />

      <section className="payouts-history">
        <header className="payouts-section-heading payouts-table-heading">
          <div><span className="payouts-section-icon"><Receipt size={15} /></span><div><span className="payouts-overline">Revisionsfähiger Verlauf</span><h2>Abrechnungshistorie</h2><p>State Machine: calculated → ready → exported → paid.</p></div></div>
          <div className="payouts-history-tools">
            <div className="payouts-history-filters">{HISTORY_FILTERS.map((filter) => <button className={historyFilter === filter.key ? 'is-active' : ''} type="button" key={filter.key} onClick={() => setHistoryFilter(filter.key)}>{filter.label}<b>{filter.key === 'all' ? payouts.length : payouts.filter((p) => p.status === filter.key).length}</b></button>)}</div>
            <label className="payouts-search"><Search size={14} /><span className="sr-only">Historie durchsuchen</span><input type="search" value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} placeholder="Partner, Zeitraum, Batch…" /></label>
          </div>
        </header>

        {selectedForExport.length > 0 && <div className="payouts-create"><div><strong>{selectedForExport.length} freigegebene Periode(n)</strong><p>pain.001 wird als kontrollierte Datei gespeichert; es wird keine Überweisung ausgelöst.</p></div><button type="button" onClick={exportSepa} disabled={exporting}><Download size={13} /> {exporting ? 'Exportiert…' : 'SEPA-Datei erzeugen'}</button></div>}

        <div className="payouts-table-wrap">
          <table>
            {/* „Zahlungsdienstleisterkosten" ist der Wortlaut des verbindlichen Belegs
                (backend/src/lib/financeDocuments.js: pspLabel). „PSP-Kosten" stand in keinem
                Dokument, das ein Unternehmer abheftet — und „Kartengebühren" wäre zu eng:
                das Feld trägt auch die Kosten aus Guthabenkäufen. Die Erklärzeile steht
                direkt unter dem Namen, wortgleich zum Partner-Portal. */}
            <thead><tr>
              {['Export', 'Partner / Zeitraum', 'Auszahlungsbetrag', 'Take + Service'].map((h) => <th key={h}>{h}</th>)}
              <th className="payouts-th-psp">Zahlungsdienstleisterkosten<small>Was der Zahlungsdienstleister für den Einzug berechnet.</small></th>
              {['Status', 'Dokumente', 'Nächster Schritt'].map((h) => <th key={h}>{h}</th>)}
            </tr></thead>
            <tbody>
              {shownPayouts.length === 0 ? <tr className="payouts-empty-row"><td colSpan="8">Noch keine passenden Abrechnungen</td></tr> : shownPayouts.map((payout) => {
                const breakdown = payout.breakdown || {};
                const periodBlocked = Boolean(payout.period_conflict) || payout.finance_operable !== true;
                const exportable = payout.can_export_sepa === true;
                // „Übertrag" durfte hier nicht stehen: im verbindlichen PDF derselben Woche
                // heißt „Übertrag negativer Saldo" den carry_IN (financeDocuments.js),
                // hier steht der carry_OUT. Ein Wort, zwei verschiedene Zahlen. Der Name ist
                // jetzt „Schlusssaldo" — derselbe wie im Partner-Portal. Das Vorzeichen wird
                // gedreht, weil carry_out als POSITIVE Schuld gespeichert und als NEGATIVER
                // carry_in der Folgewoche zurückgegeben wird (financeLedger.js).
                return (
                  <tr key={payout.id}>
                    <td><input aria-label={`SEPA ${payout.company_name}`} type="checkbox" disabled={!exportable} checked={selectedForExport.includes(payout.id)} onChange={() => setSelectedForExport((ids) => ids.includes(payout.id) ? ids.filter((id) => id !== payout.id) : [...ids, payout.id])} /></td>
                    <td><span className="payouts-history-partner"><strong>{payout.company_name}</strong><small>{payout.period_label}</small></span></td>
                    <td><strong className="is-payout">{money(payout.amount)}</strong><small>Schlusssaldo {cents(payout.carry_out_cents ? -payout.carry_out_cents : 0)}</small></td>
                    <td>{cents((breakdown.takeGrossCents || 0) + (breakdown.serviceGrossCents || 0))}</td>
                    <td>{cents(payout.psp_cost_cents ? -payout.psp_cost_cents : 0)}</td>
                    <td><span className={`payouts-status ${payout.status === 'paid' ? 'is-paid' : 'is-pending'}`}>{payout.status === 'paid' ? <CheckCircle2 size={12} /> : <Clock3 size={12} />}{STATE_LABELS[payout.status] || payout.status}</span>{payout.batch_id && <small>{payout.batch_id}</small>}{payout.period_conflict && <small>Periodenkonflikt</small>}{payout.finance_operable !== true && <small>{payout.finance_blocked_reason || 'Finanzperiode gesperrt'}</small>}</td>
                    <td><div className="payouts-actions">
                      <button type="button" className="is-statement" disabled={payout.can_download_documents !== true} onClick={() => download(payout.id, 'statement', `Umsatzuebersicht-${payout.id}.pdf`)}><Receipt size={12} /> Übersicht</button>
                      <button type="button" className="is-invoice" disabled={payout.can_download_documents !== true} onClick={() => download(payout.id, 'invoice', `VOOGO-Gebuehrenrechnung-${payout.id}.pdf`)}><FileText size={12} /> Gebührenrechnung</button>
                      <button type="button" disabled={payout.can_download_csv !== true} onClick={() => download(payout.id, 'detail.csv', `Partnerdetail-${payout.id}.csv`, 'text/csv;charset=utf-8')}><FileSpreadsheet size={12} /> Detail-CSV</button>
                      <button type="button" disabled={payout.can_download_csv !== true} onClick={() => download(payout.id, 'admin.csv', `Admin-Auszahlung-${payout.id}.csv`, 'text/csv;charset=utf-8')}><FileSpreadsheet size={12} /> Admin-CSV</button>
                    </div></td>
                    <td><button type="button" className="payouts-review-open" aria-label={`Prüfen: ${payout.company_name}, ${localDate(payout.period_start)} – ${localDate(payout.period_end)}`} onClick={() => setReviewPayout(payout)}><Search size={12} /> Prüfen</button>{periodBlocked ? <small>Explizite Periodenklärung erforderlich</small> : <>{payout.status === 'calculated' && <button type="button" disabled={creating} onClick={() => createPayout(payout.partner_id)}>Ältesten Prüfstand erstellen</button>}{payout.status === 'ready' && (exportable ? <small>Für SEPA auswählen</small> : Number(payout.payout_cents) === 0 ? <button type="button" className="is-paid-action" onClick={() => { setPaidCandidate(payout); setPaidReference('Nullwoche · keine Auszahlung'); }}><CheckCircle2 size={12} /> Nullwoche abschließen</button> : <small>Kein serverseitig freigegebener Export</small>)}{payout.can_confirm_paid === true && <button type="button" className="is-paid-action" onClick={() => { setPaidCandidate(payout); setPaidReference(''); }}><CheckCircle2 size={12} /> Zahlung bestätigen</button>}{payout.status === 'paid' && <small>Referenz: {payout.paid_reference}</small>}</>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {matchingPayouts.length > historyLimit && <div className="payouts-load-more"><span>Noch {matchingPayouts.length - historyLimit} Vorgänge</span><button type="button" onClick={() => setHistoryLimit((n) => n + 25)}>Weitere 25 anzeigen</button></div>}
      </section>

      {reviewPayout && <PayoutReview payout={reviewPayout} onClose={() => setReviewPayout(null)} />}

      {paidCandidate && (
        <div className="payouts-paid-overlay" role="presentation">
          <div className="payouts-paid-dialog" role="dialog" aria-modal="true" aria-labelledby="paid-title">
            <h2 id="paid-title">Auszahlung verbindlich bestätigen</h2>
            <p>{paidCandidate.company_name} · Auszahlungsbetrag {money(paidCandidate.amount)}</p>
            <label><span>Bank-/Buchungsreferenz</span><input autoFocus value={paidReference} onChange={(e) => setPaidReference(e.target.value)} placeholder="Pflichtfeld, z. B. Bankreferenz" /></label>
            <p><strong>Diese Aktion bestätigt den exportierten Batch als bezahlt.</strong> Sie löst keine Überweisung aus.</p>
            <div><button type="button" onClick={() => { setPaidCandidate(null); setPaidReference(''); }}>Abbrechen</button><button type="button" disabled={!paidReference.trim()} onClick={confirmPaid}>Mit Referenz bestätigen</button></div>
          </div>
        </div>
      )}
      {rejectCandidate && (
        <div className="payouts-paid-overlay" role="presentation">
          <div className="payouts-paid-dialog" role="dialog" aria-modal="true" aria-labelledby="reject-title">
            <h2 id="reject-title">Prüfstand ablehnen</h2>
            <p>{rejectCandidate.company_name} · {rejectCandidate.period_label}</p>
            <label><span>Pflichtgrund</span><input autoFocus value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} /></label>
            <div><button type="button" onClick={() => setRejectCandidate(null)}>Abbrechen</button><button type="button" disabled={!rejectReason.trim()} onClick={rejectReview}>Mit Grund ablehnen</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
